import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { db } from '../firebase';
import { collection, addDoc, query, where, getDocs, updateDoc, serverTimestamp, doc } from 'firebase/firestore';
import '../styles/Login.css';

const Signup = () => {
  const [email, setEmail] = useState('');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [invitedBy, setInvitedBy] = useState('');
  const navigate = useNavigate();
  const location = useLocation();
  const { signup, currentUser, signInWithGoogle, updateProfile } = useAuth();

  useEffect(() => {
    if (currentUser) {
      navigate('/contacts');
    }

    const params = new URLSearchParams(location.search);
    const fromEmail = params.get('from');
    if (fromEmail) {
      setInvitedBy(fromEmail);
    }
  }, [currentUser, navigate, location]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (password.length < 6) {
      setError('Password must be at least 6 characters long');
      return;
    }

    try {
      setLoading(true);
      const userCredential = await signup(email, password);
      if (username) {
        await updateProfile(userCredential.user, {
          displayName: username
        });
      }

      // Update contact status if user was invited
      if (invitedBy) {
        const contactsRef = collection(db, 'contacts');
        const q = query(
          contactsRef,
          where('from', '==', invitedBy),
          where('to', '==', email)
        );
        
        const querySnapshot = await getDocs(q);
        if (!querySnapshot.empty) {
          // Update the contact status to active
          const contactDoc = querySnapshot.docs[0];
          await updateDoc(doc(db, 'contacts', contactDoc.id), {
            status: 'active',
            lastSeen: serverTimestamp(),
            online: true
          });

          // Create a chat room
          const chatRoomRef = await addDoc(collection(db, 'chatRooms'), {
            participants: [email, invitedBy],
            createdAt: serverTimestamp(),
            lastMessage: null,
            lastMessageTime: null
          });

          // Create a reciprocal contact for the new user
          await addDoc(collection(db, 'contacts'), {
            from: email,
            to: invitedBy,
            status: 'active',
            createdAt: serverTimestamp(),
            online: true,
            lastSeen: serverTimestamp(),
            blocked: false,
            muted: false,
            chatRoomId: chatRoomRef.id
          });

          // Update the original contact with the chat room ID
          await updateDoc(doc(db, 'contacts', contactDoc.id), {
            chatRoomId: chatRoomRef.id
          });
        }
      }

      navigate('/contacts');
    } catch (error) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignUp = async () => {
    setError('');
    setLoading(true);
    try {
      await signInWithGoogle();
      navigate('/', { replace: true });
    } catch (error) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-container">
      <div className="login-box">
        <h2>Create Account</h2>
        <p className="subtitle">Sign up to get started</p>
        <div className="error-message" style={{ color: '#dc2425' }}>{error}</div>
        <form onSubmit={handleSubmit}>
          <div className="input-group">
            <input
              type="email"
              placeholder="Email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              disabled={loading}
            />
            <i className="fas fa-envelope"></i>
          </div>

          <div className="input-group">
            <input
              type="text"
              placeholder="Username (optional)"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              disabled={loading}
            />
            <i className="fas fa-user"></i>
          </div>
          
          <div className="input-group">
            <input
              type="password"
              placeholder="Password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              disabled={loading}
            />
            <i className="fas fa-lock"></i>
          </div>

          <div className="input-group">
            <input
              type="password"
              placeholder="Confirm Password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
              disabled={loading}
            />
            <i className="fas fa-lock"></i>
          </div>
          
          <button 
            type="submit" 
            className="sign-in-button"
            disabled={loading}
          >
            {loading ? 'Creating Account...' : 'Create Account'}
          </button>
        </form>
        
        <div className="divider">
          <span>OR CONTINUE WITH</span>
        </div>
        
        <button 
          onClick={handleGoogleSignUp} 
          className="google-sign-in"
          disabled={loading}
        >
          <img src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg" alt="Google" />
          Sign up with Google
        </button>
        
        <p className="signup-link">
          Already have an account? <a href="/login">Sign in</a>
        </p>
      </div>
    </div>
  );
};

export default Signup;
